import React, { useState, useEffect } from 'react';
import { useLang } from '../i18n/LanguageContext';

export const Navigation = () => {
  const { lang, toggle, t } = useLang();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['hero', 'about', 'projects', 'resume', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { id: 'hero', label: t.nav.home },
    { id: 'about', label: t.nav.about },
    { id: 'projects', label: t.nav.projects },
    { id: 'resume', label: t.nav.resume },
    { id: 'contact', label: t.nav.contact },
  ];

  const LangToggle = ({ mobile = false }) => (
    <button
      onClick={toggle}
      aria-label={t.nav.langLabel}
      data-testid="lang-toggle"
      className={`text-xs font-semibold tracking-wider uppercase transition-colors ${
        mobile ? 'px-4 py-2' : 'ml-2'
      } text-muted-foreground hover:text-foreground`}
    >
      <span className={lang === 'es' ? 'text-foreground' : ''}>ES</span>
      <span className="mx-1 text-muted">/</span>
      <span className={lang === 'en' ? 'text-foreground' : ''}>EN</span>
    </button>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/80 backdrop-blur-md border-b border-border shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <button
            onClick={() => scrollToSection('hero')}
            className="text-lg font-semibold tracking-tight text-foreground hover:text-accent transition-colors"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="nav-logo"
          >
            Portfolio
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`text-sm font-medium transition-colors relative ${
                  activeSection === link.id
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-${link.id}`}
              >
                {link.label}
                {activeSection === link.id && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-accent" />
                )}
              </button>
            ))}
            <LangToggle />
          </div>

          {/* Mobile right cluster */}
          <div className="md:hidden flex items-center gap-2">
            <LangToggle mobile />
            <button
              className="text-foreground p-2"
              onClick={() => {
                const mobileMenu = document.getElementById('mobile-menu');
                if (mobileMenu) {
                  mobileMenu.classList.toggle('hidden');
                }
              }}
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          id="mobile-menu"
          className="hidden md:hidden pb-4 space-y-2 animate-in fade-in slide-in-from-top-4 duration-300"
        >
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => {
                scrollToSection(link.id);
                document.getElementById('mobile-menu')?.classList.add('hidden');
              }}
              className={`block w-full text-left px-4 py-2 text-sm font-medium rounded transition-colors ${
                activeSection === link.id
                  ? 'text-foreground bg-muted/30'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};
